import {
    createContext,
    forwardRef,
    Fragment,
    ReactElement,
    Ref,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { isInStudio, type BlockDefinition, type BlockInstance, type PageRendererArgs } from "@softr-io/blocks-sdk";
import { Block } from "./Block";
import { rootBlocks } from "./block-root";
import { useNestedBlockMap, useScrollToBlock } from "./hooks";
import { useAppConstants } from "./AppConstantsContext";
import { PageContextProvider } from "./PageContext";
import { useSPANavigation, useSPANavigationProgress } from "./spa";
import { useDeepMemo } from "./useDeepMemo";
import { FooterBoundBanner } from "@/softr-branding/FooterBoundBanner";
import { FloatingBadge } from "@/softr-branding/FloatingBadge";
import { assert, isInModal } from "@softr/utils";
import { DialogsAndModals } from "./DialogsAndModals";
import { setupModalRecordListener } from "./modalRecordStore";
import { setupRecordsByBlockListener } from "./recordsByBlockStore";
import { AppContext } from "./AppContext";
import { PwaController } from "./pwa/PwaController";
import { MfaLostAccessPromptController } from "./mfa/MfaLostAccessPromptController";
import { useBlockReadiness } from "./useBlockReadiness";
import { BlockLoader } from "./BlockLoader";
import { useModalNavHistory } from "./openInModal";

/**
 * Here's the rough structure of the app root.
 * // Exists to avoid prop-drilling
 * <AppConstantsProvider>
 *    <AppRoot>
 *      // Exists because `Page` component can't receive props becuase it's rendered by react-router.
 *      <AppRootContextProvider>
 *         <LegacyAppRoot or AppRootWithFixedBlocks>
 *            <PageRoot>
 *              // Exists because ChildBlock component needs access to stuff which the Blocks don't pass down
 *              <PageContextProvider>
 *                blocks.map((block) => <Block />
 *              </PageContextProvider>
 *            </PageRoot>
 *         </LegacyAppRoot or AppRootWithFixedBlocks>
 *      </AppRootContextProvider>
 *    </AppRoot>
 * </AppConstantsProvider>
 */

export type AppRootProps = Readonly<{
    definitions: ReadonlyArray<BlockDefinition>;
    pageId: string;
    appHeaderCustomCode: string;
    pageHeaderCustomCode: string;
    pageFooterCustomCode: string;
    theme: PageRendererArgs["theme"];
    context: PageRendererArgs["context"];
}>;

export type PageRef = Readonly<{
    blockInstanceMap: Map<string, BlockInstance>;
}>;

interface AppRootContextValue {
    theme: PageRendererArgs["theme"];
    pageRef: Ref<PageRef>;
}
/**
 * The reason why this context exists is because react-router route components can't receive
 * any props. They can only access values through contexts.
 * Most of the "static" stuff lies in the AppConstantsContext, most of the page specific stuff
 * lies in the PageContext. For anything else that's required to construct the page context,
 * we need this context.
 */
const AppRootContext = createContext<AppRootContextValue | null>(null);
function useAppRootContext(): AppRootContextValue {
    const context = useContext(AppRootContext);
    if (!context) {
        throw new Error("useAppRootContext must be used within an AppRootContextProvider");
    }
    return context;
}

export const AppRoot = forwardRef<PageRef, AppRootProps>(function Page(props, forwardedRef) {
    const { spaMode } = useAppConstants();
    useModalNavHistory();
    const appRootContextValue = useMemo<AppRootContextValue>(
        () => ({
            theme: props.theme,
            pageRef: forwardedRef,
        }),
        [props.theme, forwardedRef],
    );
    let element: ReactElement;
    if (spaMode) {
        element = <SPAAppRoot {...props} />;
    } else {
        // Not SPA, but still fixed block roots
        element = <PageRoot definitions={props.definitions} pageId={props.pageId} />;
    }

    // The value of application_context which is passed down by the studio changes on every render
    // even when its contents are the same. This is why we need to deep memo here.
    // Otherwise, memo(Block) will be useless because this context is used by every block.
    const appContextValue = useDeepMemo(props.context.application_context);

    return (
        <AppContext.Provider value={appContextValue}>
            <AppRootContext.Provider value={appRootContextValue}>{element}</AppRootContext.Provider>
        </AppContext.Provider>
    );
});

const SPAAppRoot = (props: AppRootProps) => {
    const navigation = useSPANavigation({
        initialPageId: props.pageId,
        initialPageDefinitions: props.definitions,
        appHeaderCustomCode: props.appHeaderCustomCode,
        initialPageHeaderCustomCode: props.pageHeaderCustomCode,
        initialPageFooterCustomCode: props.pageFooterCustomCode,
    });
    return <PageRoot pageId={navigation.pageId} definitions={navigation.definitions} />;
};

interface PageRootProps {
    pageId: string;
    definitions: ReadonlyArray<BlockDefinition>;
}

function PageRoot({ definitions, pageId }: PageRootProps) {
    const { featureFlags, showMadeWithBadge, badgeType, aiApp, mode } = useAppConstants();
    const { theme, pageRef } = useAppRootContext();

    const rootDefinitions = rootBlocks(definitions);
    const nestedBlocks = useNestedBlockMap(definitions);
    const accent = theme.defaultButtonBackgroundColor;
    const showMadeWith = showMadeWithBadge && !isInModal();

    // Listen for records forwarded up from "Open page" modal iframes
    useEffect(() => {
        if (isInModal()) return;
        return setupModalRecordListener();
    }, []);

    // Listen for each dynamic block's rows so the AgentDialog panel can read them at send
    // time (see recordsByBlockStore). Keyed on the hrid set so it only re-subscribes when the
    // page's blocks actually change, not on every re-render that hands us a new array.
    const dynamicHridsKey = useMemo(
        () =>
            definitions
                .filter((b) => b.type === "dynamic")
                .map((b) => b.hrid)
                .sort()
                .join("\n"),
        [definitions],
    );
    useEffect(() => {
        if (isInModal()) return;
        return setupRecordsByBlockListener(dynamicHridsKey ? dynamicHridsKey.split("\n") : []);
    }, [dynamicHridsKey]);

    let contents: ReactElement;
    const navBlock = definitions.find((def) => def.name === "navigation");
    const { isBlockReady } = useBlockReadiness();
    const nonNavBlocks = rootDefinitions.filter(
        (def) => def.name !== "navigation" && !def.name.startsWith("header") && !def.name.startsWith("footer"),
    );
    const firstNonNavBlockReady = nonNavBlocks[0] ? isBlockReady(nonNavBlocks[0]) : true;
    const lastNonNavBlockId = nonNavBlocks.length > 0 ? nonNavBlocks[nonNavBlocks.length - 1]?.id : undefined;
    const allBlocksReady = nonNavBlocks.every(isBlockReady);

    const [timeoutElapsed, setTimeoutElapsed] = useState(false);
    useEffect(() => {
        const timeout = setTimeout(() => {
            setTimeoutElapsed(true);
        }, 3_000);
        return () => clearTimeout(timeout);
    }, [definitions]);

    // Until the timeout is elapsed:
    // 1. we want to show a single loader below the last block that's ready
    // 2. we want to hide all blocks that are below the first block that's not ready to ensure
    //    that the blocks render in order.
    //
    // After the time out, we show a separate loader for each block to prevent a single slow block from
    // blocking the rendering of all blocks below it.
    const Loader = useCallback(
        ({ blockId }: { blockId: string }) => {
            // In the studio, each block has its own loader.
            // In platform, we render a single loader after the last block
            if (mode === "studio") {
                return null;
            }
            const currentIndex = definitions.findIndex((def) => def.id === blockId);
            assert(currentIndex !== -1, `Block definition with id ${blockId} not found`);
            const def = definitions[currentIndex];
            assert(def, `Block definition with id ${blockId} not found`);
            if (timeoutElapsed) {
                const previousBlock = definitions[currentIndex - 1];
                // Collapse consecutive spinners into a single one
                if (previousBlock && !isBlockReady(previousBlock)) {
                    return null;
                } else {
                    return !isBlockReady(def) && <BlockLoader variant="tall" />;
                }
            } else {
                // Show a loader just before the footer
                if (allBlocksReady) {
                    return null;
                } else {
                    if (def.id === lastNonNavBlockId) {
                        return (
                            <BlockLoader
                                variant={
                                    // Show a tall loader when no blocks are ready, otherwise, show a short loader
                                    // after the last ready block
                                    firstNonNavBlockReady ? "short" : "tall"
                                }
                            />
                        );
                    } else {
                        return null;
                    }
                }
            }
        },
        [mode, timeoutElapsed, definitions, allBlocksReady, isBlockReady, lastNonNavBlockId, firstNonNavBlockReady],
    );
    if (featureFlags.navigationLayout || navBlock) {
        const nonNavRootDefinitions = rootDefinitions.filter((def) => def.name !== "navigation");
        const firstPendingIndex = nonNavRootDefinitions.findIndex((def) => !isBlockReady(def));

        contents = (
            <>
                {navBlock && (
                    <Block
                        definition={navBlock}
                        childBlocks={nestedBlocks[navBlock.id]}
                        childBlockRootProps={null}
                        topLevel
                        index={0}
                    />
                )}
                <main id="main-content">
                    <PageNavigationLoader accent={accent} />

                    {nonNavRootDefinitions.map((def, index) => (
                        <Fragment
                            // Making def.name and def.version is a temporary hack
                            // It forces the entire block to re-initialize when the version changes.
                            // When we change to a page with a different version of the same block
                            // in the same slot, there's a moment where React18Block will try to
                            // render the new block definition with an old version of the
                            // block component.
                            // TODO: Fix the race condition inside React18Block so that this
                            // hack is no longer necessary.
                            key={`${def.id}-${def.name}-${def.version}`}
                        >
                            <Block
                                index={index}
                                definition={def}
                                childBlocks={nestedBlocks[def.id]}
                                childBlockRootProps={null}
                                hiddenDueToPreviousBlockLoading={
                                    !timeoutElapsed && index > firstPendingIndex && firstPendingIndex !== -1
                                }
                                topLevel
                            />
                            <Loader blockId={def.id} />
                        </Fragment>
                    ))}
                    {showMadeWith && badgeType === "FOOTER_BOUND" && (
                        <FooterBoundBanner
                            theme={theme}
                            lastBlockDef={nonNavRootDefinitions[nonNavRootDefinitions.length - 1]}
                        />
                    )}

                    {showMadeWith && badgeType === "FLOATING_BADGE" && <FloatingBadge aiApp={aiApp} />}
                </main>
            </>
        );
    } else {
        const firstPendingIndex = rootDefinitions.findIndex((def) => !isBlockReady(def));
        contents = (
            <>
                <PageNavigationLoader accent={accent} />
                {rootDefinitions.map((def, index) => (
                    <Fragment
                        // Making def.name and def.version is a temporary hack
                        // It forces the entire block to re-initialize when the version changes.
                        // When we change to a page with a different version of the same block
                        // in the same slot, there's a moment where React18Block will try to
                        // render the new block definition with an old version of the
                        // block component.
                        // TODO: Fix the race condition inside React18Block so that this
                        // hack is no longer necessary.
                        key={`${def.id}-${def.name}-${def.version}`}
                    >
                        <Block
                            definition={def}
                            index={index}
                            childBlocks={nestedBlocks[def.id]}
                            childBlockRootProps={null}
                            hiddenDueToPreviousBlockLoading={
                                !timeoutElapsed && index > firstPendingIndex && firstPendingIndex !== -1
                            }
                            topLevel
                        />
                        <Loader blockId={def.id} />
                    </Fragment>
                ))}
                {showMadeWith && badgeType === "FOOTER_BOUND" && (
                    <FooterBoundBanner theme={theme} lastBlockDef={rootDefinitions[rootDefinitions.length - 1]} />
                )}

                {showMadeWith && badgeType === "FLOATING_BADGE" && <FloatingBadge aiApp={aiApp} />}
            </>
        );
    }

    return (
        <PageContextProvider
            pageId={pageId}
            definitions={definitions}
            pageRef={pageRef}
            nestedBlocks={nestedBlocks}
            theme={theme}
        >
            {contents}
            {!isInStudio() && <ScrollToBlock definitions={definitions} />}
            {!isInStudio() && <PwaController definitions={definitions} theme={theme} />}
            {!isInStudio() && <MfaLostAccessPromptController theme={theme} definitions={definitions} />}
            <DialogsAndModals theme={theme} definitions={definitions} />
        </PageContextProvider>
    );
}
const ScrollToBlock = ({ definitions }: { definitions: ReadonlyArray<BlockDefinition> }) => {
    useScrollToBlock({ definitions });
    return null;
};

function PageNavigationLoader({ accent }: { accent: string }) {
    const progress = useSPANavigationProgress();
    if (progress <= 0 || progress >= 100) return null;
    return (
        <div
            style={{
                zIndex: 10000,
                position: "fixed",
                top: 0,
                left: 0,
                height: "3px",
                backgroundColor: "transparent",
                width: "100%",
            }}
        >
            <div
                style={{
                    height: "100%",
                    backgroundColor: accent,
                    width: `${progress}%`,
                    transition: "width 0.1s ease-in-out",
                    boxShadow: `0 0 10px ${accent}`,
                }}
            />
        </div>
    );
}
